import React, { useEffect } from 'react';
import { getSeoConfigForPath, DOMAIN } from '../utils/seoMetadata';

export interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: string;
  category?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
}) => {
  useEffect(() => {
    const currentPath = path || window.location.pathname || '/';
    const fallbackConfig = getSeoConfigForPath(currentPath);

    const fullTitle = title 
      ? (title.includes('AzuraOS') ? title : `${title} | AzuraOS`)
      : fallbackConfig.title;

    const metaDescription = description || fallbackConfig.description;
    const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
    const canonicalUrl = `${DOMAIN}${cleanPath === '/' ? '' : cleanPath}`;
    
    // Select image: explicit prop -> path-specific preset -> default
    const rawImage = image || fallbackConfig.image;
    const fullImageUrl = rawImage.startsWith('http') 
      ? rawImage 
      : `${DOMAIN}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;

    // 1. Update Document Title
    document.title = fullTitle;

    // Helper function to update or create meta tags
    const updateMeta = (selector: string, attrName: string, attrValue: string, content: string) => {
      let element = document.head.querySelector(`${selector}[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update or create link tags (e.g. canonical)
    const updateLink = (rel: string, href: string) => {
      let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute(rel, rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Primary Meta Tags
    updateMeta('meta', 'name', 'title', fullTitle);
    updateMeta('meta', 'name', 'description', metaDescription);
    updateLink('canonical', canonicalUrl);

    // 3. Open Graph / Facebook / LinkedIn / WhatsApp
    updateMeta('meta', 'property', 'og:site_name', 'AzuraOS');
    updateMeta('meta', 'property', 'og:type', type);
    updateMeta('meta', 'property', 'og:url', canonicalUrl);
    updateMeta('meta', 'property', 'og:title', fullTitle);
    updateMeta('meta', 'property', 'og:description', metaDescription);
    updateMeta('meta', 'property', 'og:image', fullImageUrl);
    updateMeta('meta', 'property', 'og:image:url', fullImageUrl);
    updateMeta('meta', 'property', 'og:image:secure_url', fullImageUrl);
    updateMeta('meta', 'property', 'og:image:type', 'image/png');
    updateMeta('meta', 'property', 'og:image:width', '1200');
    updateMeta('meta', 'property', 'og:image:height', '630');
    updateMeta('meta', 'property', 'og:image:alt', fullTitle);
    updateMeta('meta', 'property', 'og:locale', 'en_US');
    updateMeta('meta', 'property', 'og:updated_time', new Date().toISOString());

    // Legacy image_src for LinkedIn
    updateLink('image_src', fullImageUrl);

    // 4. Twitter Card Tags (X / Twitter)
    updateMeta('meta', 'name', 'twitter:card', 'summary_large_image');
    updateMeta('meta', 'name', 'twitter:site', '@AzuraOSapp');
    updateMeta('meta', 'name', 'twitter:creator', '@AzuraOSapp');
    updateMeta('meta', 'name', 'twitter:url', canonicalUrl);
    updateMeta('meta', 'name', 'twitter:title', fullTitle);
    updateMeta('meta', 'name', 'twitter:description', metaDescription);
    updateMeta('meta', 'name', 'twitter:image', fullImageUrl);
    updateMeta('meta', 'name', 'twitter:image:src', fullImageUrl);
    updateMeta('meta', 'name', 'twitter:image:alt', fullTitle);

    // 5. Dynamic Structured Data (JSON-LD) for Search Indexing
    let jsonLdScript = document.head.querySelector('script#azuraos-jsonld') as HTMLScriptElement | null;
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.id = 'azuraos-jsonld';
      jsonLdScript.type = 'application/ld+json';
      document.head.appendChild(jsonLdScript);
    }

    const jsonLdData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebApplication',
          '@id': `${DOMAIN}/#webapp`,
          'name': 'AzuraOS',
          'url': DOMAIN,
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'All',
          'browserRequirements': 'Requires HTML5, JavaScript enabled',
          'description': metaDescription,
          'image': fullImageUrl,
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD',
            'description': '7-Day Free Trial of Sovereign Founder Pro'
          }
        },
        {
          '@type': 'WebSite',
          '@id': `${DOMAIN}/#website`,
          'url': DOMAIN,
          'name': 'AzuraOS',
          'description': metaDescription,
          'publisher': {
            '@type': 'Organization',
            'name': 'AzuraOS',
            'url': DOMAIN,
            'logo': `${DOMAIN}/og-image.png`
          }
        },
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          'url': canonicalUrl,
          'name': fullTitle,
          'description': metaDescription,
          'primaryImageOfPage': {
            '@type': 'ImageObject',
            'url': fullImageUrl
          },
          'isPartOf': {
            '@id': `${DOMAIN}/#website`
          }
        }
      ]
    };

    jsonLdScript.textContent = JSON.stringify(jsonLdData, null, 2);

    // Broadcast a custom event for diagnostic inspectors
    window.dispatchEvent(new CustomEvent('azuraos_seo_updated', {
      detail: {
        title: fullTitle,
        description: metaDescription,
        url: canonicalUrl,
        image: fullImageUrl,
        path: cleanPath
      }
    }));

  }, [title, description, path, image, type]);

  return null;
};

export default SEO;
